import { ChangeDetectorRef, Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { CompanySelectionDescriptionDTO } from '@iota/iota-management-portal-v4-client';
import { IotaModalOptions, IotaModalSize, MatDialogRef } from '@iota/iota-ui-components';
import { Store } from '@ngrx/store';
import {
    AddUpdateRemoveCompanySelectionDescriptions,
    LoadCompanySelectionDescriptionByCompanyId
} from 'src/app/store/actions/company-selection-description.actions';
import {
    allCompanySelectionDescriptionsByCompany,
    companySelectionDescriptionsLoadedByCompany,
    companySelectionDescriptionsLoading
} from 'src/app/store/reducer/company-selection-description.reducer';
import { selectedCompanyId } from 'src/app/store/store';
import { getValueFromStore, waitForBooleanValue } from '../../utils';
import { SelectionDescriptionComponent } from './selection-description/selection-description.component';

export interface ConfigureSelectionDescriptionDialogResult {
    reload: boolean;
}

@Component({
    standalone: false,
    selector: 'app-dialog-configure-selection-descriptions',
    templateUrl: './dialog-configure-selection-descriptions.component.html',
    styleUrls: ['./dialog-configure-selection-descriptions.component.scss']
})
export class DialogConfigureSelectionDescriptionsComponent implements OnInit {
    public modalOptions: Partial<IotaModalOptions> = {
        size: IotaModalSize.LARGE,
        showToggleFullScreenButton: true
    };

    @ViewChildren('selectionDescriptions') selectionDescriptionChildren: QueryList<SelectionDescriptionComponent>;

    saveLoading = false;
    loaded = false;

    allSelectionDescriptions: CompanySelectionDescriptionDTO[] = [];

    constructor(
        private ref: ChangeDetectorRef,
        private store: Store,
        private dialogRef: MatDialogRef<DialogConfigureSelectionDescriptionsComponent>
    ) {}

    ngOnInit(): void {
        this.initValues();
    }

    async initValues() {
        const companyId = await getValueFromStore(this.store, selectedCompanyId);
        this.loaded = await getValueFromStore(this.store, companySelectionDescriptionsLoadedByCompany(companyId));

        if (!this.loaded) {
            this.store.dispatch(LoadCompanySelectionDescriptionByCompanyId({ companyId }));
            await waitForBooleanValue(this.store, companySelectionDescriptionsLoading, false);
            this.loaded = true;
        }

        this.allSelectionDescriptions = await getValueFromStore(this.store, allCompanySelectionDescriptionsByCompany(companyId));
    }

    async addNewSelectionDescription() {
        const companyId = await getValueFromStore(this.store, selectedCompanyId);

        const newDescription: CompanySelectionDescriptionDTO = {
            companyId
        };

        if (!this.allSelectionDescriptions) {
            this.allSelectionDescriptions = [];
        }

        this.allSelectionDescriptions.unshift(newDescription);
        this.ref.detectChanges();
    }

    deleteSelectionDescription(description: CompanySelectionDescriptionDTO) {
        if (!description.companySelectionDescriptionId && this.allSelectionDescriptions.includes(description)) {
            this.allSelectionDescriptions.splice(this.allSelectionDescriptions.indexOf(description), 1);
        }
    }

    async onSaveClick() {
        const children = this.selectionDescriptionChildren.toArray();
        const upsertedDescriptions = children.map((desc) => desc.getUpdatedSelectionDescription()).filter((desc) => !!desc);
        const deletedDescriptionIds = children.map((desc) => desc.getDeletedSelectionDescriptionIds()).filter((id) => !!id);

        const companyId = await getValueFromStore(this.store, selectedCompanyId);

        this.store.dispatch(
            AddUpdateRemoveCompanySelectionDescriptions({
                upsertedCompanySelectionDescriptions: {
                    updatedCompanySelectionDescriptions: upsertedDescriptions,
                    removedCompanySelectionDescriptionIds: deletedDescriptionIds
                },
                companyId
            })
        );

        this.saveLoading = true;
        await waitForBooleanValue(this.store, companySelectionDescriptionsLoading, false);
        this.saveLoading = false;

        const result: ConfigureSelectionDescriptionDialogResult = {
            reload: true
        };
        this.dialogRef.close(result);
    }

    onCancelClick() {
        this.dialogRef.close();
    }
}
